import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  Grid
} from '@mui/material';
import {
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Schedule as ScheduleIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { usePermissions } from '../../hooks/usePermissions';

interface Database {
  name: string;
  size?: number;
  included?: boolean;
}

interface BackupHistory {
  _id: string;
  filename: string;
  filepath: string;
  size: number;
  type: 'auto' | 'manual' | 'pre-restore';
  status: 'success' | 'failed' | 'in-progress';
  compressed: boolean;
  databases?: Database[];
  errorMessage?: string;
  createdBy?: {
    _id: string;
    username: string;
  };
  createdAt: string;
  restoredAt?: string;
  restoredBy?: {
    _id: string;
    username: string;
  };
}

interface BackupSchedule {
  _id: string;
  frequency: 'daily' | 'weekly';
  time: string;
  dayOfWeek: number;
  isActive: boolean;
  keepLastBackups: number;
  lastRun?: string;
  nextRun?: string;
  createdBy?: {
    _id: string;
    username: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface BackupStats {
  totalBackups: number;
  successfulBackups: number;
  failedBackups: number;
  totalSize: number;
  totalSizeMB: string;
  lastBackup?: BackupHistory;
}

const BackupTab: React.FC = () => {
  const { hasPermission } = usePermissions();
  
  // Check permissions
  const canAddSchedule = hasPermission('settings', 'advanced-settings', 'add_schedule');
  const canEditSchedule = hasPermission('settings', 'advanced-settings', 'edit_schedule');
  const canToggleSchedule = hasPermission('settings', 'advanced-settings', 'toggle_schedule');
  const canDeleteSchedule = hasPermission('settings', 'advanced-settings', 'delete_schedule');
  const canCreateBackup = hasPermission('settings', 'advanced-settings', 'create_backup');
  const canRestoreExternal = hasPermission('settings', 'advanced-settings', 'restore_external');
  const canRestoreBackup = hasPermission('settings', 'advanced-settings', 'restore_backup');
  const canDownloadBackup = hasPermission('settings', 'advanced-settings', 'download_backup');
  const canDeleteBackup = hasPermission('settings', 'advanced-settings', 'delete_backup');
  
  const [backups, setBackups] = useState<BackupHistory[]>([]);
  const [schedules, setSchedules] = useState<BackupSchedule[]>([]);
  const [stats, setStats] = useState<BackupStats | null>(null);
  const [availableDatabases, setAvailableDatabases] = useState<Database[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<BackupHistory | null>(null);
  const [selectedDatabases, setSelectedDatabases] = useState<string[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFilePath, setUploadFilePath] = useState<string | null>(null);
  const [uploadDatabases, setUploadDatabases] = useState<Database[]>([]);
  const [uploadStep, setUploadStep] = useState<'select' | 'inspect'>('select');
  
  // Schedule form states
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [scheduleFrequency, setScheduleFrequency] = useState<'daily' | 'weekly'>('daily');
  const [scheduleTime, setScheduleTime] = useState('02:00');
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState(0);
  const [scheduleKeepLast, setScheduleKeepLast] = useState(7);
  const [scheduleActive, setScheduleActive] = useState(true);

  useEffect(() => {
    fetchBackups();
    fetchSchedule();
    fetchStats();
    fetchAvailableDatabases();
  }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/backups/list');
      setBackups(response.data.backups);
    } catch (err: any) {
      console.error('Error fetching backups:', err);
      setError(err.response?.data?.message || 'فشل تحميل قائمة النسخ الاحتياطية');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedule = async () => {
    try {
      const response = await axiosInstance.get('/backups/schedule');
      if (response.data.schedules && response.data.schedules.length > 0) {
        setSchedules(response.data.schedules);
      } else {
        setSchedules([]);
      }
    } catch (err: any) {
      console.error('Error fetching schedule:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/backups/stats');
      setStats(response.data.stats);
    } catch (err: any) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await axiosInstance.post('/backups/create');
      setSuccess(response.data.message);
      
      await fetchBackups();
      await fetchStats();
    } catch (err: any) {
      console.error('Error creating backup:', err);
      setError(err.response?.data?.message || 'فشل إنشاء النسخة الاحتياطية');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      if (editingScheduleId) {
        await axiosInstance.put(`/backups/schedule/${editingScheduleId}`, {
          frequency: scheduleFrequency,
          time: scheduleTime,
          dayOfWeek: scheduleDayOfWeek,
          isActive: scheduleActive,
          keepLastBackups: scheduleKeepLast
        });
      } else {
        await axiosInstance.post('/backups/schedule', {
          frequency: scheduleFrequency,
          time: scheduleTime,
          dayOfWeek: scheduleDayOfWeek,
          isActive: scheduleActive,
          keepLastBackups: scheduleKeepLast
        });
      }
      
      setSuccess(editingScheduleId ? 'تم تعديل الجدولة بنجاح' : 'تم إضافة الجدولة بنجاح');
      setOpenScheduleDialog(false);
      setEditingScheduleId(null);
      
      await fetchSchedule();
    } catch (err: any) {
      console.error('Error saving schedule:', err);
      setError(err.response?.data?.message || 'فشل حفظ الجدولة');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSchedule = async (scheduleId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.patch(`/backups/schedule/${scheduleId}/toggle`);
      setSuccess(response.data.message);
      
      await fetchSchedule();
    } catch (err: any) {
      console.error('Error toggling schedule:', err);
      setError(err.response?.data?.message || 'فشل تبديل حالة الجدولة');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الجدولة؟')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      await axiosInstance.delete(`/backups/schedule/${scheduleId}`);
      setSuccess('تم حذف الجدولة بنجاح');
      
      await fetchSchedule();
    } catch (err: any) {
      console.error('Error deleting schedule:', err);
      setError(err.response?.data?.message || 'فشل حذف الجدولة');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSchedule = (schedule: BackupSchedule) => {
    setEditingScheduleId(schedule._id);
    setScheduleFrequency(schedule.frequency);
    setScheduleTime(schedule.time);
    setScheduleDayOfWeek(schedule.dayOfWeek);
    setScheduleKeepLast(schedule.keepLastBackups);
    setScheduleActive(schedule.isActive);
    setOpenScheduleDialog(true);
  };

  const handleAddSchedule = () => {
    setEditingScheduleId(null);
    setScheduleFrequency('daily');
    setScheduleTime('02:00');
    setScheduleDayOfWeek(0);
    setScheduleKeepLast(7);
    setScheduleActive(true);
    setOpenScheduleDialog(true);
  };

  const fetchAvailableDatabases = async () => {
    try {
      const response = await axiosInstance.get('/backups/databases');
      setAvailableDatabases(response.data.databases);
    } catch (err: any) {
      console.error('Error fetching databases:', err);
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await axiosInstance.post(`/backups/restore/${selectedBackup._id}`, {
        selectedDatabases: selectedDatabases.length > 0 ? selectedDatabases : null
      });
      
      setSuccess(response.data.message);
      setOpenRestoreDialog(false);
      setSelectedDatabases([]);
      
      await fetchBackups();
      await fetchStats();
    } catch (err: any) {
      console.error('Error restoring backup:', err);
      setError(err.response?.data?.message || 'فشل استعادة النسخة الاحتياطية');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDatabase = (dbName: string) => {
    setSelectedDatabases(prev => {
      if (prev.includes(dbName)) {
        return prev.filter(name => name !== dbName);
      } else {
        return [...prev, dbName];
      }
    });
  };

  const handleSelectAllDatabases = () => {
    if (selectedBackup && selectedBackup.databases) {
      setSelectedDatabases(selectedBackup.databases.map(db => db.name));
    }
  };

  const handleDeselectAllDatabases = () => {
    setSelectedDatabases([]);
  };

  const handleInspectUploadFile = async () => {
    if (!uploadFile) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('backupFile', uploadFile);
      
      const response = await axiosInstance.post('/backups/inspect-external', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadFilePath(response.data.filepath);
      setUploadDatabases(response.data.databases);
      setUploadStep('inspect');
      setSelectedDatabases([]);
      
    } catch (err: any) {
      console.error('Error inspecting backup file:', err);
      setError(err.response?.data?.message || 'فشل فحص الملف');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadAndRestore = async () => {
    if (!uploadFilePath) return;
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await axiosInstance.post('/backups/restore-external', {
        filepath: uploadFilePath,
        selectedDatabases: selectedDatabases.length > 0 ? selectedDatabases : null
      });
      
      setSuccess(response.data.message);
      setOpenUploadDialog(false);
      setUploadFile(null);
      setUploadFilePath(null);
      setUploadDatabases([]);
      setUploadStep('select');
      setSelectedDatabases([]);
      
      await fetchBackups();
      await fetchStats();
    } catch (err: any) {
      console.error('Error uploading and restoring backup:', err);
      setError(err.response?.data?.message || 'فشل استعادة النسخة الاحتياطية');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBackup = async (backupId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه النسخة الاحتياطية؟')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.delete(`/backups/${backupId}`);
      setSuccess(response.data.message);
      
      await fetchBackups();
      await fetchStats();
    } catch (err: any) {
      console.error('Error deleting backup:', err);
      setError(err.response?.data?.message || 'فشل حذف النسخة الاحتياطية');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = async (backupId: string, filename: string) => {
    try {
      const response = await axiosInstance.get(`/backups/download/${backupId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      console.error('Error downloading backup:', err);
      setError('فشل تحميل النسخة الاحتياطية');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getTypeLabel = (type: string): string => {
    const labels: { [key: string]: string } = {
      'auto': 'تلقائي',
      'manual': 'يدوي',
      'pre-restore': 'قبل الاستعادة'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: { [key: string]: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } = {
      'auto': 'primary',
      'manual': 'secondary',
      'pre-restore': 'warning'
    };
    return colors[type] || 'default';
  };

  const getDayName = (day: number): string => {
    const days = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    return days[day] || '';
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Statistics */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  إجمالي النسخ
                </Typography>
                <Typography variant="h4">
                  {stats.totalBackups}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  النسخ الناجحة
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.successfulBackups}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  النسخ الفاشلة
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.failedBackups}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  المساحة المستخدمة
                </Typography>
                <Typography variant="h4">
                  {stats.totalSizeMB} MB
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Schedule Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              جدولة النسخ الاحتياطي
            </Typography>
            {canAddSchedule ? (
              <Button
                variant="outlined"
                startIcon={<ScheduleIcon />}
                onClick={handleAddSchedule}
              >
                إضافة جدولة
              </Button>
            ) : (
              <Tooltip title="ليس لديك صلاحية إضافة جدولة">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<LockIcon />}
                    disabled
                  >
                    إضافة جدولة
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {schedules.length > 0 ? (
            <Stack spacing={2}>
              {schedules.map((schedule) => (
                <Card key={schedule._id} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Chip
                          label={schedule.isActive ? 'نشط' : 'معطل'}
                          color={schedule.isActive ? 'success' : 'default'}
                          size="small"
                        />
                        <Typography variant="subtitle1" fontWeight="medium">
                          {schedule.frequency === 'daily' ? 'يومياً' : 'أسبوعياً'}
                          {' في الساعة '}
                          {schedule.time}
                          {schedule.frequency === 'weekly' && ` - ${getDayName(schedule.dayOfWeek)}`}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary">
                        الاحتفاظ بآخر {schedule.keepLastBackups} نسخة
                      </Typography>

                      {schedule.nextRun && (
                        <Typography variant="body2" color="text.secondary">
                          التنفيذ التالي: {format(new Date(schedule.nextRun), 'dd/MM/yyyy HH:mm', { locale: ar })}
                        </Typography>
                      )}
                    </Box>
                    
                    <Stack direction="row" spacing={1}>
                      {canEditSchedule && (
                        <Tooltip title="تعديل">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditSchedule(schedule)}
                          >
                            <ScheduleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canToggleSchedule && (
                        <Tooltip title={schedule.isActive ? 'تعطيل' : 'تفعيل'}>
                          <IconButton
                            size="small"
                            color={schedule.isActive ? 'error' : 'success'}
                            onClick={() => handleToggleSchedule(schedule._id)}
                          >
                            {schedule.isActive ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDeleteSchedule && (
                        <Tooltip title="حذف">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSchedule(schedule._id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                </Card>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">
              لم يتم إعداد جدولة للنسخ الاحتياطي التلقائي
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        {canCreateBackup ? (
          <Button
            variant="contained"
            startIcon={<BackupIcon />}
            onClick={handleCreateBackup}
            disabled={loading}
          >
            إنشاء نسخة احتياطية الآن
          </Button>
        ) : (
          <Tooltip title="ليس لديك صلاحية إنشاء نسخة احتياطية">
            <span>
              <Button
                variant="contained"
                startIcon={<LockIcon />}
                disabled
              >
                إنشاء نسخة احتياطية الآن
              </Button>
            </span>
          </Tooltip>
        )}
        {canRestoreExternal ? (
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => setOpenUploadDialog(true)}
          >
            استعادة من ملف خارجي
          </Button>
        ) : (
          <Tooltip title="ليس لديك صلاحية استعادة من ملف خارجي">
            <span>
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                disabled
              >
                استعادة من ملف خارجي
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchBackups();
            fetchStats();
          }}
        >
          تحديث
        </Button>
      </Stack>

      {/* Backups Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>اسم الملف</TableCell>
              <TableCell>النوع</TableCell>
              <TableCell>الحجم</TableCell>
              <TableCell>التاريخ</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : backups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                    لا توجد نسخ احتياطية
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              backups.map((backup) => (
                <TableRow key={backup._id}>
                  <TableCell>{backup.filename}</TableCell>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(backup.type)}
                      color={getTypeColor(backup.type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatFileSize(backup.size)}</TableCell>
                  <TableCell>
                    {format(new Date(backup.createdAt), 'dd/MM/yyyy HH:mm', { locale: ar })}
                  </TableCell>
                  <TableCell>
                    {backup.status === 'success' ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="نجح"
                        color="success"
                        size="small"
                      />
                    ) : backup.status === 'failed' ? (
                      <Chip
                        icon={<ErrorIcon />}
                        label="فشل"
                        color="error"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="قيد التنفيذ"
                        color="warning"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {canRestoreBackup ? (
                        <Tooltip title="استعادة">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedBackup(backup);
                                setOpenRestoreDialog(true);
                              }}
                              disabled={backup.status !== 'success'}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="ليس لديك صلاحية استعادة النسخة">
                          <span>
                            <IconButton
                              size="small"
                              color="default"
                              disabled
                            >
                              <LockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canDownloadBackup ? (
                        <Tooltip title="تحميل">
                          <span>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleDownloadBackup(backup._id, backup.filename)}
                              disabled={backup.status !== 'success'}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="ليس لديك صلاحية تحميل النسخة">
                          <span>
                            <IconButton
                              size="small"
                              color="default"
                              disabled
                            >
                              <LockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canDeleteBackup ? (
                        <Tooltip title="حذف">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteBackup(backup._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="ليس لديك صلاحية حذف النسخة">
                          <span>
                            <IconButton
                              size="small"
                              color="default"
                              disabled
                            >
                              <LockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Schedule Dialog */}
      <Dialog open={openScheduleDialog} onClose={() => {
        setOpenScheduleDialog(false);
        setEditingScheduleId(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingScheduleId ? 'تعديل جدولة النسخ الاحتياطي' : 'إضافة جدولة للنسخ الاحتياطي'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">التكرار</FormLabel>
              <RadioGroup
                value={scheduleFrequency}
                onChange={(e) => setScheduleFrequency(e.target.value as 'daily' | 'weekly')}
              >
                <FormControlLabel value="daily" control={<Radio />} label="يومياً" />
                <FormControlLabel value="weekly" control={<Radio />} label="أسبوعياً" />
              </RadioGroup>
            </FormControl>

            <TextField
              label="الوقت"
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            {scheduleFrequency === 'weekly' && (
              <FormControl fullWidth>
                <InputLabel>اليوم</InputLabel>
                <Select
                  value={scheduleDayOfWeek}
                  onChange={(e) => setScheduleDayOfWeek(Number(e.target.value))}
                  label="اليوم"
                >
                  <MenuItem value={0}>الأحد</MenuItem>
                  <MenuItem value={1}>الاثنين</MenuItem>
                  <MenuItem value={2}>الثلاثاء</MenuItem>
                  <MenuItem value={3}>الأربعاء</MenuItem>
                  <MenuItem value={4}>الخميس</MenuItem>
                  <MenuItem value={5}>الجمعة</MenuItem>
                  <MenuItem value={6}>السبت</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              label="الاحتفاظ بآخر"
              type="number"
              value={scheduleKeepLast}
              onChange={(e) => setScheduleKeepLast(Number(e.target.value))}
              fullWidth
              InputProps={{
                endAdornment: <Typography>نسخة</Typography>
              }}
              inputProps={{ min: 1, max: 30 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={scheduleActive}
                  onChange={(e) => setScheduleActive(e.target.checked)}
                />
              }
              label="تفعيل الجدولة"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenScheduleDialog(false)}>إلغاء</Button>
          <Button onClick={handleSaveSchedule} variant="contained" disabled={loading}>
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={openRestoreDialog} onClose={() => setOpenRestoreDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>تأكيد استعادة النسخة الاحتياطية</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>تحذير:</strong> سيتم إنشاء نسخة احتياطية تلقائية من جميع قواعد البيانات الحالية قبل الاستعادة.
            </Typography>
          </Alert>
          {selectedBackup && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>اسم الملف:</strong> {selectedBackup.filename}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>التاريخ:</strong> {format(new Date(selectedBackup.createdAt), 'dd/MM/yyyy HH:mm', { locale: ar })}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>الحجم:</strong> {formatFileSize(selectedBackup.size)}
              </Typography>

              {selectedBackup.databases && selectedBackup.databases.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    اختر قواعد البيانات للاستعادة:
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button size="small" onClick={handleSelectAllDatabases}>
                      تحديد الكل
                    </Button>
                    <Button size="small" onClick={handleDeselectAllDatabases}>
                      إلغاء التحديد
                    </Button>
                  </Stack>
                  <Stack spacing={1}>
                    {selectedBackup.databases.map((db) => (
                      <FormControlLabel
                        key={db.name}
                        control={
                          <Switch
                            checked={selectedDatabases.includes(db.name)}
                            onChange={() => handleToggleDatabase(db.name)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1">{db.name}</Typography>
                            {db.size && (
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(db.size)}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    ))}
                  </Stack>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {selectedDatabases.length === 0 ? (
                      <Typography variant="body2">
                        سيتم استعادة جميع قواعد البيانات ({selectedBackup.databases.length} قاعدة)
                      </Typography>
                    ) : (
                      <Typography variant="body2">
                        سيتم استعادة {selectedDatabases.length} قاعدة بيانات محددة
                      </Typography>
                    )}
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenRestoreDialog(false);
            setSelectedDatabases([]);
          }}>
            إلغاء
          </Button>
          <Button onClick={handleRestoreBackup} variant="contained" color="warning" disabled={loading}>
            استعادة
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={openUploadDialog} onClose={() => {
        setOpenUploadDialog(false);
        setUploadFile(null);
        setUploadFilePath(null);
        setUploadDatabases([]);
        setUploadStep('select');
        setSelectedDatabases([]);
      }} maxWidth="md" fullWidth>
        <DialogTitle>استعادة من ملف خارجي</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>تحذير:</strong> سيتم إنشاء نسخة احتياطية تلقائية من جميع قواعد البيانات الحالية قبل الاستعادة.
            </Typography>
          </Alert>
          
          {uploadStep === 'select' ? (
            <Box sx={{ mt: 2 }}>
              <input
                type="file"
                accept=".gz,.zip"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setUploadFile(e.target.files[0]);
                  }
                }}
                style={{ display: 'none' }}
                id="backup-file-input"
              />
              <label htmlFor="backup-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  اختر ملف النسخة الاحتياطية (.zip أو .gz)
                </Button>
              </label>
              {uploadFile && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  الملف المختار: {uploadFile.name}
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>الملف:</strong> {uploadFile?.name}
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                <strong>عدد قواعد البيانات:</strong> {uploadDatabases.length}
              </Typography>
              
              {uploadDatabases.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    اختر قواعد البيانات للاستعادة:
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button size="small" onClick={handleSelectAllDatabases}>
                      تحديد الكل
                    </Button>
                    <Button size="small" onClick={handleDeselectAllDatabases}>
                      إلغاء التحديد
                    </Button>
                  </Stack>
                  <Stack spacing={1}>
                    {uploadDatabases.map((db) => (
                      <FormControlLabel
                        key={db.name}
                        control={
                          <Switch
                            checked={selectedDatabases.includes(db.name)}
                            onChange={() => handleToggleDatabase(db.name)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1">{db.name}</Typography>
                            {db.size && (
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(db.size)}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    ))}
                  </Stack>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {selectedDatabases.length === 0 ? (
                      <Typography variant="body2">
                        سيتم استعادة جميع قواعد البيانات ({uploadDatabases.length} قاعدة)
                      </Typography>
                    ) : (
                      <Typography variant="body2">
                        سيتم استعادة {selectedDatabases.length} قاعدة بيانات محددة
                      </Typography>
                    )}
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {uploadStep === 'inspect' && (
            <Button onClick={() => {
              setUploadStep('select');
              setUploadFile(null);
              setUploadFilePath(null);
              setUploadDatabases([]);
              setSelectedDatabases([]);
            }}>
              رجوع
            </Button>
          )}
          <Button onClick={() => {
            setOpenUploadDialog(false);
            setUploadFile(null);
            setUploadFilePath(null);
            setUploadDatabases([]);
            setUploadStep('select');
            setSelectedDatabases([]);
          }}>
            إلغاء
          </Button>
          {uploadStep === 'select' ? (
            <Button
              onClick={handleInspectUploadFile}
              variant="contained"
              disabled={!uploadFile || loading}
            >
              التالي
            </Button>
          ) : (
            <Button
              onClick={handleUploadAndRestore}
              variant="contained"
              color="warning"
              disabled={loading}
            >
              استعادة
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BackupTab;